'use client';

import { useCallback, useMemo } from 'react';
import { isNil } from 'es-toolkit';
import {
  createParser,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';

import type { AccountsTableColumn } from './accounts-columns';
import { ACCOUNTS_COLUMN_TO_SLUG, ACCOUNTS_SLUG_TO_COLUMN } from './accounts-columns';

const strategyTypeValues = ['first_party', 'third_party', 'unassigned'] as const;
const sortByValues = [
  'displayName',
  'displayBalance',
  'household',
  'startDate',
  'cashAndMoneyMarketAmount',
  'cashAndMoneyMarketPercent',
] as const;
const sortByOrderValues = ['asc', 'desc'] as const;

export type StrategyType = (typeof strategyTypeValues)[number];
export type SortBy = (typeof sortByValues)[number];
export type SortByOrder = (typeof sortByOrderValues)[number];

/**
 * Pure helper that computes the next state in the Cash & Money Market column's
 * 5-state sort cycle:
 *
 *   unsorted → $ desc → $ asc → % desc → % asc → unsorted
 *
 * Any `sortBy` that is not one of the cash/MM keys is treated as "unsorted"
 * from the cash/MM column's perspective.
 */
export function nextCashMmSortState({
  sortBy,
  sortByOrder,
}: {
  sortBy: SortBy | null;
  sortByOrder: SortByOrder | null;
}): { sortBy: SortBy | null; sortByOrder: SortByOrder | null } {
  if (sortBy === 'cashAndMoneyMarketAmount' && sortByOrder === 'desc') {
    return { sortBy: 'cashAndMoneyMarketAmount', sortByOrder: 'asc' };
  }
  if (sortBy === 'cashAndMoneyMarketAmount' && sortByOrder === 'asc') {
    return { sortBy: 'cashAndMoneyMarketPercent', sortByOrder: 'desc' };
  }
  if (sortBy === 'cashAndMoneyMarketPercent' && sortByOrder === 'desc') {
    return { sortBy: 'cashAndMoneyMarketPercent', sortByOrder: 'asc' };
  }
  if (sortBy === 'cashAndMoneyMarketPercent' && sortByOrder === 'asc') {
    return { sortBy: null, sortByOrder: null };
  }
  // Unsorted (or unrelated column) → start the cycle at $ desc.
  return { sortBy: 'cashAndMoneyMarketAmount', sortByOrder: 'desc' };
}

export function assertStrategyType(value: string): asserts value is StrategyType {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!strategyTypeValues.includes(value as StrategyType)) {
    throw new Error(`Invalid strategy type: ${value}`);
  }
}

export function assertSortBy(value: string): asserts value is SortBy {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!sortByValues.includes(value as SortBy)) {
    throw new Error(`Invalid sort by: ${value}`);
  }
}

export function assertSortByOrder(value: string): asserts value is SortByOrder {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (!sortByOrderValues.includes(value as SortByOrder)) {
    throw new Error(`Invalid sort by order: ${value}`);
  }
}

// Custom parser for translating between column slugs (in URL) and column names (in app)
const parseAsAccountsTableColumn = createParser({
  parse(queryValue: string): AccountsTableColumn | null {
    const column = ACCOUNTS_SLUG_TO_COLUMN[queryValue];
    return column ?? null;
  },
  serialize(value: AccountsTableColumn): string {
    return ACCOUNTS_COLUMN_TO_SLUG[value];
  },
});

export const accountsUrlParsers = {
  // Search and filters
  search: parseAsString,
  balanceMin: parseAsInteger,
  balanceMax: parseAsInteger,
  strategyType: parseAsArrayOf(parseAsStringLiteral(strategyTypeValues)).withDefault([]),
  strategy: parseAsArrayOf(parseAsString).withDefault([]),
  feeSchedule: parseAsArrayOf(parseAsString).withDefault([]),
  household: parseAsArrayOf(parseAsString).withDefault([]),
  advisorIds: parseAsArrayOf(parseAsString).withDefault([]),
  includeUnassignedAdvisor: parseAsBoolean.withDefault(false),
  portfolioMustBePresent: parseAsBoolean.withDefault(true),
  providerNames: parseAsArrayOf(parseAsString).withDefault([]),

  // Sorting
  sortBy: parseAsStringLiteral(sortByValues),
  sortByOrder: parseAsStringLiteral(sortByOrderValues),

  // Pagination
  pageSize: parseAsInteger.withDefault(25),
  after: parseAsString,
  before: parseAsString,

  // Columns
  columns: parseAsArrayOf(parseAsAccountsTableColumn).withDefault([]),
} as const;

export function useAccountsParams() {
  const [params, setParams] = useQueryStates(accountsUrlParsers);

  const updateParams = useCallback(
    (updates: Partial<typeof params>) => {
      setParams({
        before: null,
        after: null,
        ...updates,
      });
    },
    [setParams],
  );

  const helpers = useMemo(
    () => ({
      setSearch: (value: string) => updateParams({ search: value }),
      setBalanceRange: (min: number | null, max: number | null) => updateParams({ balanceMin: min, balanceMax: max }),
      setBalanceMin: (min: number | null) => updateParams({ balanceMin: min }),
      setBalanceMax: (max: number | null) => updateParams({ balanceMax: max }),
      setStrategyType: (value: StrategyType[]) => updateParams({ strategyType: value }),
      setStrategy: (value: string[]) => updateParams({ strategy: value }),
      setFeeSchedule: (value: string[]) => updateParams({ feeSchedule: value }),
      setHousehold: (value: string[]) => updateParams({ household: value }),
      setAdvisorIds: (value: string[]) => updateParams({ advisorIds: value }),
      setIncludeUnassignedAdvisor: (value: boolean) => updateParams({ includeUnassignedAdvisor: value }),
      setPortfolioMustBePresent: (value: boolean) => updateParams({ portfolioMustBePresent: value }),
      setProviderNames: (value: string[]) => {
        if (value.length === 0) {
          updateParams({ providerNames: ['Charles Schwab', 'Fidelity', 'Interactive Brokers'] });
        } else {
          updateParams({ providerNames: value });
        }
      },
      setSortBy: (newSortBy: SortBy) => {
        if (params.sortBy === newSortBy) {
          const newOrder = params.sortByOrder === 'asc' ? 'desc' : 'asc';
          updateParams({ sortByOrder: newOrder });
        } else {
          updateParams({ sortBy: newSortBy, sortByOrder: 'asc' });
        }
      },
      setSortByOrder: (newSortByOrder: SortByOrder) => updateParams({ sortByOrder: newSortByOrder }),
      cycleCashMmSort: () => {
        const next = nextCashMmSortState({
          sortBy: params.sortBy,
          sortByOrder: params.sortByOrder,
        });
        updateParams({ sortBy: next.sortBy, sortByOrder: next.sortByOrder });
      },
      setPageSize: (size: number) => updateParams({ pageSize: size }),
      setAfter: (value: string | null) => updateParams({ after: value }),
      setBefore: (value: string | null) => updateParams({ before: value }),
      resetPagination: () => updateParams({ after: null, before: null }),
      setColumns: (columns: AccountsTableColumn[]) => {
        updateParams({ columns });
      },
      clearAllFilters: () =>
        updateParams({
          search: null,
          balanceMin: null,
          balanceMax: null,
          strategyType: [],
          strategy: [],
          feeSchedule: [],
          household: [],
          advisorIds: [],
          includeUnassignedAdvisor: false,
          portfolioMustBePresent: true,
          providerNames: [],
          before: null,
          after: null,
        }),
    }),
    [updateParams, params.sortBy, params.sortByOrder],
  );

  const pagination = useMemo(
    () => ({
      first: !isNil(params.before) ? null : params.pageSize,
      last: !isNil(params.before) ? params.pageSize : null,
      after: !isNil(params.before) ? null : params.after,
      before: !isNil(params.before) ? params.before : null,
    }),
    [params],
  );

  return {
    params,
    updateParams,
    ...helpers,
    pagination,
  };
}
